import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ISideNavigationConfig } from '@shared/modules/side-navigation/interfaces/side-navigation-config.interface';
import { projectPanelPreviewSideConfig } from '@modules/projects/modules/project-preview/components/project-panel-preview/project-panel-preview-side-config.const';
import { TranslateService } from '@ngx-translate/core';
import { NavigateService } from '@shared/services/navigate.service';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectPreviewService } from '@modules/projects/modules/project-preview/services/project-preview.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ProjectStoreService } from '@modules/projects/shared/services/project-store.service';
import { StorageService } from '@core/services/storage.service';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { ProjectController } from '@modules/projects/shared/controllers/project.controller';
import {
  ProjectChangeStageEvent,
  ProjectChangeStageEventType
} from '@modules/projects/shared/components/project-lost-stage-modal/project-lost-stage-modal.component';
import { Subscription } from 'rxjs';
import { ProjectPreviewEventType } from '../../enums/projcet-preview-event-type.enum';
import { filter, tap } from 'rxjs/operators';
import { TaskType } from '@shared/enums/task-type.enum';
import { SnackBarService } from '@core/services/snackbar.service';

@Component({
  selector: 'project-panel-preview',
  templateUrl: './project-panel-preview.component.html',
  styleUrls: ['./project-panel-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectPanelPreviewComponent implements OnInit, AfterViewInit, OnDestroy {
  config: ISideNavigationConfig;
  hiddenSideNavigation: boolean = false;
  ButtonTypes = ButtonTypes;
  ProjectStage = ProjectStage;
  ButtonSize = ButtonSize;
  Object = Object;
  subs: Subscription = new Subscription();
  taskType: TaskType;
  actionButtonText: string;
  actionButtonIcon: string;
  actionButtonPermission: any;

  private s = inject(SnackBarService);

  @ViewChild('headerTemplate', { read: TemplateRef }) headerTemplate;

  constructor(
    private t: TranslateService,
    public n: NavigateService,
    public projectPreviewService: ProjectPreviewService,
    public projectStore: ProjectStoreService,
    private route: ActivatedRoute,
    private changes: ChangeDetectorRef,
    private storage: StorageService,
    private router: Router
  ) {}

  get isDnDView(): boolean {
    return (
      this.taskType === TaskType.TYPE_TASK ||
      this.taskType === TaskType.TYPE_ORDER ||
      this.taskType === TaskType.TYPE_CORRECTION
    );
  }

  ngOnInit(): void {
    const routeSub = this.route.data.subscribe((data: { project: Project }) => {
      this.projectPreviewService.project = data.project;
      this.initSideNavigationConfig();
      this.changes.detectChanges();
    });

    const routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(
        tap(() => {
          const currentRoute = this.getDeepestChild(this.route);
          this.setDataFromRoute(currentRoute);
          const el = document.querySelector('.side-navigation-view__content');
          if (el) el.scrollTop = 0;
        })
      )
      .subscribe();

    this.setDataFromRoute(this.getDeepestChild(this.route));

    this.subs.add(routeSub);
    this.subs.add(routerSub);
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) route = route.firstChild;
    return route;
  }

  private setDataFromRoute(route: ActivatedRoute): void {
    this.taskType = route.snapshot.data['type'];
    this.actionButtonText = route.snapshot.data['actionButtonText'];
    this.actionButtonIcon = route.snapshot.data['actionButtonIcon'];
    this.actionButtonPermission = this.projectPreviewService.getActionButtonPermission(
      route.snapshot.data['metaTitle']
    );
  }

  initSideNavigationConfig() {
    this.config = projectPanelPreviewSideConfig(
      this.t,
      this.n,
      this.projectPreviewService.project,
      this.storage.Employee
    );
    this.config.headerTemplate = this.headerTemplate;
    this.changes.detectChanges();
  }

  ngAfterViewInit(): void {
    this.config.headerTemplate = this.headerTemplate;
    this.changes.detectChanges();
  }

  back() {
    this.n.go('projects-list', {}, { queryParams: { tour: null }, queryParamsHandling: 'preserve' });
  }

  onStageChange(stage: ProjectStage) {
    const ctrl = new ProjectController(this.projectPreviewService.project);
    const sub = ctrl
      .setStage(
        this.projectPreviewService.project,
        stage,
        this.projectPreviewService.project.basicDataBox.termStart,
        this.projectPreviewService.project.basicDataBox.termEnd
      )
      .subscribe((e: ProjectChangeStageEvent) => {
        switch (e.type) {
          case ProjectChangeStageEventType.CHANGED:
            this.projectPreviewService.project = e.project;
            this.s.success(this.t.instant('Projects.PreviewHeader.stageUpdated'));
            break;
        }
        this.projectPreviewService.projectServiceEventEmitter.emit({
          type: ProjectPreviewEventType.CHANGE_STAGE
        });
      });

    this.subs.add(sub);
  }

  ngOnDestroy() {
    this.subs?.unsubscribe();
    this.projectPreviewService.clearService();
    this.projectPreviewService.project = null;
  }
}
